import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SeoProps {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string;
  jsonLd?: Record<string, any>;
}

const Seo: React.FC<SeoProps> = ({ title, description, canonical, keywords, jsonLd }) => {
  const location = useLocation();
  const fullUrl = canonical || `https://pharmelo.in${location.pathname}`;

  useEffect(() => {
    document.title = title;

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement;
      if (el) {
        el.setAttribute('content', content);
      } else {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };

    setMeta('name', 'description', description);
    if (keywords) setMeta('name', 'keywords', keywords);

    // Canonical
    let linkCan = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (linkCan) {
      linkCan.setAttribute('href', fullUrl);
    } else {
      linkCan = document.createElement('link');
      linkCan.rel = 'canonical';
      linkCan.href = fullUrl;
      document.head.appendChild(linkCan);
    }

    // Open Graph
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', fullUrl);

    // Twitter
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:url', fullUrl);

    // Per-page JSON-LD
    let scriptEl = document.querySelector('script[data-page-jsonld]') as HTMLScriptElement;
    if (jsonLd) {
      if (scriptEl) {
        scriptEl.textContent = JSON.stringify(jsonLd);
      } else {
        scriptEl = document.createElement('script');
        scriptEl.type = 'application/ld+json';
        scriptEl.setAttribute('data-page-jsonld', 'true');
        scriptEl.textContent = JSON.stringify(jsonLd);
        document.head.appendChild(scriptEl);
      }
    } else if (scriptEl) {
      scriptEl.remove();
    }

    return () => {
      const leftover = document.querySelector('script[data-page-jsonld]');
      if (leftover) leftover.remove();
    };
  }, [title, description, fullUrl, keywords, jsonLd]);

  return null;
};

export default Seo;
